import React, { useState } from 'react';
import { Lock, Unlock, AlertTriangle, CheckCircle, Loader2, Puzzle, Hash, Calculator } from 'lucide-react';
import { Button } from '@/components/common';

// Local type definition (mirrors @gps-hunt/shared)
type ChallengeType = 'riddle' | 'hex_code' | 'numerical';

interface ChallengeState {
  pinId: string;
  isActive: boolean;
  lockedByUserId?: string;
  lockedByDisplayName?: string;
  lockedAt?: string;
  isSolved: boolean;
  solvedByUserId?: string;
  solvedByDisplayName?: string;
  solvedAt?: string;
}

interface HunterChallengeModalProps {
  pinId: string;
  pinIndex: number;
  challengeType: ChallengeType;
  challengeQuestion: string;
  challengeState: ChallengeState | null;
  currentUserId: string;
  onLock: () => Promise<void>;
  onUnlock: () => Promise<void>;
  onSubmit: (answer: string) => Promise<{ correct: boolean; message: string }>;
  onClose: () => void;
}

/**
 * Modal for hunters to attempt pin challenges
 */
export const HunterChallengeModal: React.FC<HunterChallengeModalProps> = ({
  pinId: _pinId,
  pinIndex,
  challengeType,
  challengeQuestion,
  challengeState,
  currentUserId,
  onLock,
  onUnlock,
  onSubmit,
  onClose,
}) => {
  const [answer, setAnswer] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLocking, setIsLocking] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [showUnlockAnimation, setShowUnlockAnimation] = useState(false);

  const hasLock = challengeState?.lockedByUserId === currentUserId;
  const isLockedByOther = challengeState?.lockedByUserId && challengeState.lockedByUserId !== currentUserId;
  const isSolved = challengeState?.isSolved;

  const handleLock = async () => {
    setIsLocking(true);
    setFeedback(null);
    try {
      await onLock();
    } catch (err) {
      setFeedback({ type: 'error', message: 'Failed to lock challenge' });
    } finally {
      setIsLocking(false);
    }
  };

  const handleUnlock = async () => {
    setIsLocking(true);
    try {
      await onUnlock();
    } catch (err) {
      setFeedback({ type: 'error', message: 'Failed to unlock' });
    } finally {
      setIsLocking(false);
    }
  };

  const handleSubmit = async () => {
    if (!answer.trim()) return;
    
    setIsSubmitting(true);
    setFeedback(null);
    try {
      const result = await onSubmit(answer.trim());
      if (result.correct) {
        setFeedback({ type: 'success', message: result.message });
        // Show unlock celebration animation
        setShowUnlockAnimation(true);
        // Auto close after animation
        setTimeout(() => onClose(), 3000);
      } else {
        setFeedback({ type: 'error', message: result.message });
        setAnswer('');
      }
    } catch (err) {
      setFeedback({ type: 'error', message: 'Failed to submit answer' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPlaceholder = () => {
    switch (challengeType) {
      case 'riddle':
        return 'Enter one word answer';
      case 'hex_code':
        return 'Enter 4-character code (e.g., A7F2)';
      case 'numerical':
        return 'Enter number';
      default:
        return 'Enter answer';
    }
  };

  const getChallengeTypeLabel = () => {
    switch (challengeType) {
      case 'riddle':
        return 'RIDDLE';
      case 'hex_code':
        return 'HEX CODE';
      case 'numerical':
        return 'NUMERICAL';
      default:
        return 'CHALLENGE';
    }
  };

  // Unlock celebration animation
  if (showUnlockAnimation) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-black/95">
        {/* Dynamic styles for unlock animation */}
        <style>{`
          @keyframes unlockBurst {
            0% { transform: scale(0); opacity: 1; }
            100% { transform: scale(5); opacity: 0; }
          }
          @keyframes successPulse {
            0%, 100% { transform: scale(1); filter: drop-shadow(0 0 20px rgba(34, 197, 94, 0.6)); }
            50% { transform: scale(1.1); filter: drop-shadow(0 0 40px rgba(34, 197, 94, 0.9)); }
          }
          @keyframes checkmarkDraw {
            0% { stroke-dashoffset: 50; }
            100% { stroke-dashoffset: 0; }
          }
          @keyframes celebrateText {
            0% { opacity: 0; transform: translateY(30px) scale(0.5); }
            50% { transform: translateY(-10px) scale(1.1); }
            100% { opacity: 1; transform: translateY(0) scale(1); }
          }
          @keyframes sparkleFloat {
            0% { transform: translateY(0) scale(1); opacity: 1; }
            100% { transform: translateY(-100px) scale(0); opacity: 0; }
          }
          @keyframes confettiFall {
            0% { transform: translateY(-100px) rotate(0deg); opacity: 1; }
            100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
          }
        `}</style>

        {/* Burst rings */}
        {[0, 0.1, 0.2].map((delay, i) => (
          <div
            key={i}
            className="absolute w-32 h-32 rounded-full border-4 border-green-500 pointer-events-none"
            style={{ animation: `unlockBurst 1s ease-out ${delay}s forwards` }}
          />
        ))}

        {/* Confetti particles */}
        {Array.from({ length: 15 }).map((_, i) => (
          <div
            key={`confetti-${i}`}
            className="absolute w-3 h-3 rounded-full"
            style={{
              left: `${10 + Math.random() * 80}%`,
              top: '-20px',
              backgroundColor: ['#22c55e', '#fbbf24', '#3b82f6', '#ec4899', '#8b5cf6'][i % 5],
              animation: `confettiFall ${2 + Math.random() * 2}s ease-out ${Math.random() * 0.5}s forwards`,
            }}
          />
        ))}

        {/* Central content */}
        <div className="relative z-10 flex flex-col items-center text-center px-6">
          {/* Unlocked icon */}
          <div className="relative mb-6">
            <div
              className="text-green-500"
              style={{
                animation: 'successPulse 1s ease-in-out infinite',
                filter: 'drop-shadow(0 0 30px rgba(34, 197, 94, 0.8))',
              }}
            >
              <Unlock className="w-24 h-24" />
            </div>

            {/* Sparkles */}
            {[0, 60, 120, 180, 240, 300].map((angle, i) => (
              <div
                key={angle}
                className="absolute w-2 h-2 bg-yellow-400 rounded-full"
                style={{
                  top: '50%',
                  left: '50%',
                  transform: `rotate(${angle}deg) translateY(-60px)`,
                  animation: `sparkleFloat 1.5s ease-out ${i * 0.1}s infinite`,
                  boxShadow: '0 0 10px rgba(250, 204, 21, 0.8)',
                }}
              />
            ))}
          </div>

          {/* Success checkmark */}
          <div className="mb-4">
            <svg width="80" height="80" viewBox="0 0 80 80">
              <circle
                cx="40"
                cy="40"
                r="35"
                fill="none"
                stroke="rgba(34, 197, 94, 0.3)"
                strokeWidth="4"
              />
              <circle
                cx="40"
                cy="40"
                r="35"
                fill="none"
                stroke="#22c55e"
                strokeWidth="4"
                strokeDasharray="220"
                strokeDashoffset="220"
                style={{ animation: 'checkmarkDraw 0.8s ease-out 0.3s forwards' }}
              />
              <path
                d="M25 42 L35 52 L55 28"
                fill="none"
                stroke="#22c55e"
                strokeWidth="5"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="50"
                strokeDashoffset="50"
                style={{ animation: 'checkmarkDraw 0.5s ease-out 0.6s forwards' }}
              />
            </svg>
          </div>

          {/* Success text */}
          <div
            style={{
              animation: 'celebrateText 0.6s ease-out 0.4s forwards',
              opacity: 0,
            }}
          >
            <h1 className="text-3xl font-bold text-green-400 mb-2">UNLOCKED!</h1>
            <p className="text-xl text-green-300">Challenge Complete!</p>
            {feedback && (
              <p className="text-gray-400 mt-2">{feedback.message}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 pb-safe-bottom">
      <div className="bg-secondary rounded-2xl w-full max-w-md p-6 animate-slide-up max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-center gap-2 mb-4">
          <Lock className="w-5 h-5 text-primary" />
          <h2 className="text-xl font-semibold">PIN {pinIndex + 1} CHALLENGE</h2>
        </div>

        {/* Challenge type badge */}
        <div className="flex justify-center mb-4">
          <span className="px-3 py-1 bg-primary/20 text-primary rounded-full text-sm font-medium flex items-center gap-1.5">
            {challengeType === 'riddle' && <Puzzle className="w-4 h-4" />}
            {challengeType === 'hex_code' && <Hash className="w-4 h-4" />}
            {challengeType === 'numerical' && <Calculator className="w-4 h-4" />}
            {getChallengeTypeLabel()}
          </span>
        </div>

        {/* Already solved message */}
        {isSolved && (
          <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-4 mb-4 text-center">
            <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-2" />
            <p className="text-green-300 font-medium">Challenge Completed!</p>
            <p className="text-sm text-text-secondary mt-1">
              Solved by {challengeState?.solvedByDisplayName}
            </p>
          </div>
        )}

        {/* Locked by other message */}
        {isLockedByOther && !isSolved && (
          <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-4 mb-4 text-center">
            <AlertTriangle className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
            <p className="text-yellow-300 font-medium">Challenge In Progress</p>
            <p className="text-sm text-text-secondary mt-1">
              {challengeState?.lockedByDisplayName} is attempting this challenge
            </p>
          </div>
        )}

        {/* Challenge content */}
        {!isSolved && (
          <>
            {/* Question */}
            <div className="bg-background rounded-lg p-4 mb-4">
              <p className="text-text-primary text-center">{challengeQuestion}</p>
            </div>

            {/* Not locked yet - show lock button */}
            {!hasLock && !isLockedByOther && (
              <div className="text-center">
                <p className="text-sm text-text-secondary mb-4">
                  Lock the challenge to begin your attempt. Only one hunter can attempt at a time.
                </p>
                <Button 
                  onClick={handleLock} 
                  disabled={isLocking}
                  className="w-full"
                >
                  {isLocking ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Locking...
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      START CHALLENGE
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Has lock - show input */}
            {hasLock && (
              <div className="space-y-4">
                <div>
                  <input
                    type={challengeType === 'numerical' ? 'number' : 'text'}
                    value={answer}
                    onChange={(e) => setAnswer(
                      challengeType === 'hex_code' 
                        ? e.target.value.toUpperCase() 
                        : e.target.value
                    )}
                    placeholder={getPlaceholder()}
                    maxLength={challengeType === 'hex_code' ? 4 : undefined}
                    className={`w-full px-4 py-3 rounded-lg bg-background text-text-primary 
                               text-center text-xl placeholder:text-text-muted ${
                      challengeType === 'hex_code' ? 'uppercase font-mono tracking-wider' : ''
                    }`}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSubmit();
                      }
                    }}
                  />
                </div>

                {/* Feedback */}
                {feedback && (
                  <div className={`p-3 rounded-lg text-center ${
                    feedback.type === 'success' 
                      ? 'bg-green-500/20 text-green-300' 
                      : 'bg-red-500/20 text-red-300'
                  }`}>
                    {feedback.message}
                  </div>
                )}

                <div className="flex gap-3">
                  <Button 
                    variant="secondary" 
                    onClick={handleUnlock}
                    disabled={isLocking || isSubmitting}
                    className="flex-1"
                  >
                    <Unlock className="w-4 h-4 mr-1" />
                    Release
                  </Button>
                  <Button 
                    onClick={handleSubmit}
                    disabled={isSubmitting || !answer.trim()}
                    className="flex-1"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Checking...
                      </>
                    ) : (
                      'SUBMIT'
                    )}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Close button for solved or locked by other */}
        {(isSolved || isLockedByOther) && (
          <Button 
            variant="secondary" 
            onClick={onClose}
            className="w-full mt-4"
          >
            Close
          </Button>
        )}
      </div>
    </div>
  );
};

export default HunterChallengeModal;
